<script lang="ts">
export default {
  name: 'EpicRuleAccordion',
};
</script>

<script setup lang="ts">
import { computed } from 'vue';
import { ChevronDown, CheckCircle2 } from '@lucide/vue';
import { formatCurrency } from '@/utilities/Money';
import type { RuleViolatingPolicy } from '@/services/v2/ClientReadinessService';

export type EpicRuleKey = 'invalidPolicyNumber' | 'noPremiumOrFee' | 'invalidDates';

const props = defineProps<{
  ruleKey: EpicRuleKey;
  title: string;
  criteria: string;
  policies: RuleViolatingPolicy[];
  isOpen: boolean;
}>();

const emit = defineEmits<{
  toggle: [];
}>();

const count = computed(() => props.policies.length);
const hasIssues = computed(() => count.value > 0);

function formatDate(value: string | null | undefined): string {
  if (!value) return '';
  return value.slice(0, 10);
}

function billModeLabel(code: string | null | undefined): string {
  if (code === 'A') return 'Agency';
  if (code === 'D') return 'Direct';
  return 'Unknown';
}

function billModeInstruction(code: string | null | undefined): string {
  if (code === 'A') return 'Ensure this policy has been invoiced in Epic. Billed Premium calculates automatically from invoicing activity.';
  if (code === 'D') return 'Update the Estimated Premium field for this policy in Epic.';
  return 'Enter a premium or broker fee for this policy in Epic.';
}
</script>

<template>
  <div class="border-b border-base-300 last:border-b-0">
    <button
      class="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-base-200/50"
      :class="{ 'bg-base-200/50': isOpen }"
      @click="emit('toggle')">
      <div
        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold"
        :class="hasIssues ? 'bg-error/15 text-error' : 'bg-success/15 text-success'">
        {{ count }}
      </div>
      <div class="flex-1">
        <div class="text-sm font-semibold">{{ title }}</div>
        <div class="text-xs text-base-content/60">{{ criteria }}</div>
      </div>
      <span
        v-if="hasIssues"
        class="badge badge-soft badge-sm badge-error">
        {{ count }} issue{{ count === 1 ? '' : 's' }}
      </span>
      <span
        v-else
        class="badge badge-soft badge-sm badge-success">
        All clear
      </span>
      <ChevronDown
        :size="16"
        class="text-base-content/40 transition-transform"
        :class="{ 'rotate-180': isOpen }" />
    </button>

    <div
      v-if="isOpen"
      class="border-t border-base-300 bg-base-200">
      <div
        v-if="!hasIssues"
        class="flex items-center gap-2 bg-base-100 px-4 py-4 text-sm text-success">
        <CheckCircle2 :size="18" />
        <template v-if="ruleKey === 'invalidPolicyNumber'">No policies with invalid policy numbers</template>
        <template v-else-if="ruleKey === 'noPremiumOrFee'">No policies with missing premium or fee</template>
        <template v-else>No policies with invalid dates</template>
      </div>
      <table
        v-else
        class="table w-full bg-base-100 table-sm">
        <thead>
          <tr class="bg-base-200 text-xs tracking-wide text-base-content/60 uppercase">
            <th>Policy description</th>
            <th v-if="ruleKey === 'invalidPolicyNumber' || ruleKey === 'noPremiumOrFee'">Policy number</th>
            <th v-if="ruleKey === 'noPremiumOrFee'">Premium</th>
            <th v-if="ruleKey === 'noPremiumOrFee'">Bill mode</th>
            <th v-if="ruleKey === 'noPremiumOrFee'">Action</th>
            <th v-if="ruleKey === 'invalidDates'">Effective date</th>
            <th v-if="ruleKey === 'invalidDates'">Expiration date</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="p in policies"
            :key="p.policyId">
            <td>{{ p.description }}</td>
            <td v-if="ruleKey === 'invalidPolicyNumber' || ruleKey === 'noPremiumOrFee'">
              <span
                v-if="!p.mainPolicyNumber || !p.mainPolicyNumber.trim()"
                class="text-base-content/40 italic"
                >&lt;blank&gt;</span
              >
              <span
                v-else
                class="font-mono text-xs"
                >{{ p.mainPolicyNumber }}</span
              >
            </td>
            <td v-if="ruleKey === 'noPremiumOrFee'">{{ formatCurrency(p.policyPremium ?? 0) }}</td>
            <td v-if="ruleKey === 'noPremiumOrFee'">{{ billModeLabel(p.billMode) }}</td>
            <td
              v-if="ruleKey === 'noPremiumOrFee'"
              class="text-xs text-base-content/70">
              {{ billModeInstruction(p.billMode) }}
            </td>
            <td v-if="ruleKey === 'invalidDates'">{{ formatDate(p.effectiveDate) || '—' }}</td>
            <td v-if="ruleKey === 'invalidDates'">{{ formatDate(p.expirationDate) || '—' }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
